import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@fpsm/cxy-components/Button';
import { Input } from '@fpsm/cxy-components/Input';
import { useFetcher } from '@remix-run/react';

import { REPUBS } from '@cx-modules/constants';

import { CX_LOGIN_SUCCESS } from '~/custom-events/CXLoginSuccessEvent';
import { type View } from '~/providers/auth-modal-provider';
import { trackEvent } from '~/utils/dataLayer';

import {
  actions,
  authProviders,
  type AuthResponse,
  events,
  views } from
'../../types';

import styles from './styles/SignupForm.module.css';

export const SignupForm = ({
  close,
  openAtView,
  repub




}: {close: () => void;openAtView: (view: View) => void;repub: string;}) => {
  const fetcher = useFetcher<AuthResponse>();
  const signupSuccessRef = useRef(false);
  const [showErrorState, setShowErrorState] = useState(false);

  const { t } = useTranslation(['label']);

  const data = fetcher.data;
  const hasErrorMessage = data?.message && !data?.success;

  // handle local error
  useEffect(() => {
    if (fetcher.state === 'idle' && (data?.fieldErrors || hasErrorMessage)) {
      setShowErrorState(true);
    }
  }, [data?.fieldErrors, hasErrorMessage, data?.message, fetcher.state]);

  // handle api success
  useEffect(() => {
    if (data?.success && !signupSuccessRef.current) {
      signupSuccessRef.current = true;
      trackEvent(events.ACCOUNT_CREATED, {
        eventAction: actions.SIGN_UP_SUCCESS,
        method: authProviders.ODDSCHECKER,
        view: views.SIGN_UP,
        userId: data?.userId
      });

      window.dispatchEvent(new CustomEvent(CX_LOGIN_SUCCESS, { detail: data }));

      close();
    }
  }, [close, data, openAtView]);

  // handle api error
  const email = fetcher.formData?.get('email');
  useEffect(() => {
    if (hasErrorMessage && email && data) {
      trackEvent(events.ACCOUNT_CREATED_ERROR, {
        eventAction: actions.SIGN_UP_ERROR,
        method: authProviders.ODDSCHECKER,
        view: views.SIGN_UP,
        email: email,
        signupError: data.message
      });
    }
  }, [hasErrorMessage, email, data]);

  const onChange = () => {
    setShowErrorState(false);
  };

  return (
    <fetcher.Form
      action={`${repub !== REPUBS.OC.name ? `/${repub.toLowerCase()}` : ''}/plus/auth/signup`}
      className={styles.signUpForm}
      method="post"
      onSubmit={() => {
        trackEvent(events.ACCOUNT_CREATED_ATTEMPT, {
          eventAction: actions.SIGN_UP,
          method: authProviders.ODDSCHECKER,
          view: views.SIGN_UP
        });
      }}>

			<Input
        aria-describedby="email-error"
        autoComplete="email"
        errors={showErrorState ? [data?.fieldErrors?.email] : undefined}
        id="email"
        key="email"
        name="email"
        onChange={onChange}
        placeholder="Email"
        type="email" />

			<Input
        aria-describedby="password-error"
        autoComplete="new-password"
        id="password"
        key="password"
        name="password"
        onChange={onChange}
        placeholder="Password"
        type="password" />

			<Input
        aria-describedby="confirmPassword-error"
        autoComplete="new-password"
        errors={
        showErrorState ?
        [data?.fieldErrors?.password, data?.fieldErrors?.confirmPassword] :
        undefined
        }
        id="confirmPassword"
        key="confirmPassword"
        name="confirmPassword"
        onChange={onChange}
        placeholder="Confirm Password"
        type="password" />

			{hasErrorMessage && showErrorState &&
      <div className="mt-2 text-red-300" id="generic-error">
					{data.message}
				</div>
      }
			<div className={styles.buttonSection}>
				<Button
          isLoading={fetcher.state !== 'idle'}
          size="large"
          type="submit"
          variant="secondary">

					{t('CX_AUTH_SIGNUP')}
				</Button>
			</div>
		</fetcher.Form>);

};

SignupForm.displayName = 'SignupForm';